import { Star } from 'lucide-react';

import { cn } from '@/lib/utils';
import { INTERVIEW_CRITERIA } from '../constants';
import type { CriteriaRating } from '../types';

interface CriteriaRatingsFieldProps {
  value: CriteriaRating[];
  onChange: (next: CriteriaRating[]) => void;
}

/** Scorecard editor — the standard criteria, each rated 1–5 with stars.
 *  Click a star to set the score; click the active score again to clear it.
 *  Only rated criteria are kept in the stored value. */
export function CriteriaRatingsField({ value, onChange }: CriteriaRatingsFieldProps) {
  const scoreFor = (criterion: string) =>
    value.find((r) => r.criterion === criterion)?.score ?? 0;

  function setScore(criterion: string, score: number) {
    const current = scoreFor(criterion);
    const rest = value.filter((r) => r.criterion !== criterion);
    // Clicking the current score clears it; otherwise set the new score.
    onChange(score === current ? rest : [...rest, { criterion, score }]);
  }

  return (
    <div className="divide-y rounded-md border">
      {INTERVIEW_CRITERIA.map((criterion) => {
        const score = scoreFor(criterion);
        return (
          <div key={criterion} className="flex items-center justify-between gap-3 px-3 py-2">
            <span className="text-sm">{criterion}</span>
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  aria-label={`${criterion}: ${n} of 5`}
                  aria-pressed={n <= score}
                  onClick={() => setScore(criterion, n)}
                  className="rounded p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Star
                    className={cn(
                      'size-4 transition-colors',
                      n <= score
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-muted-foreground/40 hover:text-amber-400',
                    )}
                  />
                </button>
              ))}
              <span className="text-muted-foreground ml-2 w-8 text-right text-xs tabular-nums">
                {score ? `${score}/5` : '—'}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
